import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { OrganizationsNewUserDialogComponent } from '../../components/new-user-dialog/new-user-dialog';
import { OrganizationsProvider, UserOrganizationRow } from '../../providers/organizations-provider';
import { OrganizationsNavigationProvider } from '../../providers/navigation-provider';
import { CurrentUserRolesProvider } from '../../providers/current-user-roles-provider';
import { OrganizationRights } from '../../providers/organization-rights';
import { BreadcrumbProvider } from '../../../breadcrumb/providers/breadcrumb-provider';
import { forkJoin, Observable, Subscription, tap } from 'rxjs';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MenuItem, ModuleMenu } from "../../../common/models/menu";
import { Organization } from "../../../common/models/organization";
import { SimpleMatDataSource } from "../../../common/providers/simple-mat-data-source";
import { OrganizationRole } from "../../../common/models/organization-role";
import { ConfigProvider } from "../../../config-provider.service";
import { CurrentUser } from "../../../common/providers/current-user";
import { UtilsProvider } from "../../../common/providers/utils-provider.service";
import { ConfirmDialogComponent } from "../../../common/components/confirm-dialog/confirm-dialog";
import {
  OrganizationMembership_OrganizationRole
} from "../../../common/models/organization-membership_organization-role";
import { map } from "rxjs/operators";
import { HeaderComponent } from "../../../common/components/header-elitment/header-elitment";
import { SideMenuComponent } from "../../../common/components/side-menu/side-menu";
import { PageContentComponent } from "../../../common/components/page-content/page-content";
import { NgForOf, NgIf } from "@angular/common";
import { ModuleContentComponent } from "../../../common/components/module-content/module-content";
import { MatFormField } from "@angular/material/form-field";
import { MatIcon } from "@angular/material/icon";
import {
  MatCell, MatCellDef,
  MatColumnDef,
  MatHeaderCell, MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow, MatRowDef,
  MatTable
} from "@angular/material/table";
import { MatOption, MatSelect } from "@angular/material/select";
import { FormsModule } from "@angular/forms";
import { MatMenu, MatMenuItem, MatMenuTrigger } from "@angular/material/menu";
import { Error401Component } from "../../../common/components/error-401/error-401";
import { FooterComponent } from "../../../common/components/footer-elitment/footer-elitment";
import { MatInput } from "@angular/material/input";
import { MatIconButton } from "@angular/material/button";
import { error } from "@angular/compiler-cli/src/transformers/util";

@Component({
  selector: 'organizations-users-page',
  providers: [],
  styleUrls: ['./users.scss'],
  standalone: true,
  imports: [
    HeaderComponent,
    SideMenuComponent,
    PageContentComponent,
    NgIf,
    ModuleContentComponent,
    MatFormField,
    TranslateModule,
    MatIcon,
    MatTable,
    MatHeaderRow,
    MatRow,
    MatHeaderCell,
    MatCell,
    MatSelect,
    FormsModule,
    MatOption,
    MatSortModule,
    MatMenuTrigger,
    MatMenu,
    MatPaginator,
    Error401Component,
    FooterComponent,
    MatInput,
    MatColumnDef,
    MatHeaderRowDef,
    MatRowDef,
    MatHeaderCellDef,
    MatCellDef,
    NgForOf,
    MatMenuItem,
    MatIconButton
  ],
  templateUrl: './users.html'
})
export class OrganizationsUsersPageComponent implements OnInit, AfterViewInit, OnDestroy {
    public backUrl: string | null = null;
    public pageTitle = 'Settings';
    public menus: MenuItem[] = [];
    public shortcutMenus: ModuleMenu[] = [];
    public isLoading = true;
    public is401 = false;

    public organization: Organization | null = null;
    public organizationRights: OrganizationRights = new OrganizationRights(this._utils);

    public dataSource: SimpleMatDataSource = new SimpleMatDataSource([]);
    public columns: string[] = ['image', 'name', 'roles', 'actions'];
    public availableRoles: OrganizationRole[] = [];

    @ViewChild(MatPaginator, {static: false}) public paginator: MatPaginator | null = null;
    @ViewChild(MatSort, {static: false}) public sorter: MatSort | null = null;

    private _pendingRolesChange: UserOrganizationRow | null = null;
    private subscriptions: Subscription[] = [];
    constructor(
        public conf: ConfigProvider,
        public breadcrumbProvider: BreadcrumbProvider,
        public currentUser: CurrentUser,
        private _title: Title,
        private _utils: UtilsProvider,
        private _currentUserRoles: CurrentUserRolesProvider,
        private _translater: TranslateService,
        private _toaster: MatSnackBar,
        private _navigation: OrganizationsNavigationProvider,
        private _dialoger: MatDialog,
        private _organizationsProvider: OrganizationsProvider,
    ) {
    }

    public ngOnInit() {
    }

    public ngAfterViewInit() {
        this.loadOrganization().subscribe({
          next: () => {
            this.loadData().subscribe({
              next: () => {
                  this.initNav();
                  this.initSorters();
                  this.isLoading = false;
              },
              error: (error) => {
                  this.onError(error);
              }
            });
          },
          error: (error) => {
            this.onError(error);
          }
        })
    }

    public ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    public onSortChange(event: any): void {
        this.dataSource.sortBy(event.active, event.direction);
    }

    public onFilterKeyup(filter: string): void {
        this.dataSource.filter = filter.trim().toLowerCase();
    }

    public onAddUserClicked(): void {
        const dialog: MatDialogRef<OrganizationsNewUserDialogComponent> = this._dialoger.open(OrganizationsNewUserDialogComponent, {
            data: {
                organization: this.organization,
                avoidedUsers: this._utils.getKeyValues(this.dataSource.data, 'user').slice()
            }
        });
        this.subscriptions.push(
            dialog.afterClosed().subscribe((data: UserOrganizationRow) => {
                if (data) {
                    this.dataSource.push(data);
                }
            })
        );
    }

    public onUserRolesChanged(row: UserOrganizationRow): void {
        this._pendingRolesChange = row;
    }

    public onUserRolesToggled(opened: boolean): void {
        if (!opened && this._pendingRolesChange) {
            this.isLoading = true;
            this.saveUserRoles(this._pendingRolesChange)
                .subscribe({
                next: () => {
                  this._pendingRolesChange = null;
                  this.isLoading = false;
                  this._toaster.open(this._translater.instant('Roles_saved'), '', {duration: 5000});
                },
                error: (error: any) => this.onError(error)
                });
        }
    }

    public onRemoveUserClicked(row: UserOrganizationRow): void {
        const dialog: MatDialogRef<any> = this._dialoger.open(ConfirmDialogComponent, {
            data: {
                confirmText: this._translater.instant('Confirm_remove_user_from_organization')
            }
        });
        this.subscriptions.push(
            dialog.afterClosed().subscribe((confirmed: boolean) => {
                if (confirmed) {
                    this.isLoading = true;
                    this.removeUser(row).subscribe({
                      next: () => {
                        this.isLoading = false;
                        this._toaster.open(this._translater.instant('User_removed'), '', {duration: 5000});
                      },
                      error: (error: any) => this.onError(error)
                    });
                }
            })
        );
    }

    private loadOrganization(): Observable<void> {
        return this._navigation.getOrganisationFromRoute().pipe(
            map((organization: Organization | null) => {
                if (organization) {
                    this.organization = organization;
                    this.organizationRights.evaluate({
                        organization: this.organization,
                        user: this.currentUser.user!,
                        rolesIds: this._currentUserRoles.getRolesIds(this.organization.id!)
                    });
                } else {
                    throw { error: 'Organization not found', status: 404 };
                }
            }),
        );
    }

    private loadData(): Observable<void> {
      return forkJoin([
          this._organizationsProvider.getOrganizationUsersRows(this.organization!),
          this._organizationsProvider.getOrganizationRoles()
      ]).pipe(
        map((data: any[]) => {
              this.dataSource.replaceAllWith(data[0]);
              this.availableRoles = data[1];
          })
      );
    }

    private initNav(): void {
        this._title.setTitle(this._translater.instant('Users') + ' | ' + this.conf.platformName);
        this.menus = this._navigation.getMenus('settings', {
            organization: this.organization!,
            rights: this.organizationRights
        });
        this.backUrl = '/' + this.organization!.code;

        this.shortcutMenus = [
            { title: 'Add', icon: 'add', url: null, onClick: () => this.onAddUserClicked() }
        ];

        this.is401 = !this.organizationRights.EDIT;
        this.dataSource.paginator = this.paginator!;
        this.dataSource.sort = this.sorter!;
        this.dataSource.filterPredicate = (row: UserOrganizationRow, filter: string): boolean => (row.user.firstName?.toLowerCase() + ' ' + row.user.lastName?.toLowerCase()).includes(filter.toLowerCase());

        this.breadcrumbProvider.setDefaultCrumbs([]);
    }

    private initSorters(): void {
        this.dataSource.addSortDefinition(
            'name',
            (a: UserOrganizationRow, b: UserOrganizationRow) => {
                const aName: string = a.user.firstName?.toLowerCase() + ' ' + a.user.lastName?.toLowerCase();
                const bName: string = b.user.firstName?.toLocaleLowerCase() + ' ' + b.user.lastName?.toLowerCase();
                if (aName < bName) { return -1; } else if (aName > bName) { return 1; }
                return 0;
            },
            (a: UserOrganizationRow, b: UserOrganizationRow) => {
                const aName: string = a.user.firstName?.toLowerCase() + ' ' + a.user.lastName?.toLowerCase();
                const bName: string = b.user.firstName?.toLocaleLowerCase() + ' ' + b.user.lastName?.toLowerCase();
                if (aName > bName) { return -1; } else if (aName < bName) { return 1; }
                return 0;
            }
        );
    }

    private saveUserRoles(row: UserOrganizationRow): Observable<any> {
      return this._organizationsProvider.saveUserRoles(row.membership.id!, row.rolesIds).pipe(
        map((newRoles: OrganizationMembership_OrganizationRole[]) => {
          for (let i = 0; i < this.dataSource.count; i++) {
            if (this.dataSource.get(i).user.id === row.user.id) {
              const newRolesData: any[] = [];
              for (let j = 0; j < newRoles.length; j++) {
                newRolesData.push({
                  role: this._utils.findIn(newRoles[j].organizationRoleId, this.availableRoles, 'id'),
                  membershipRole: newRoles[j]
                });
              }
              row.roles = newRolesData;
              this.dataSource.set(i, row);

              i = this.dataSource.count;
            }
          }
        }),
      );
    }

    private removeUser(row: UserOrganizationRow): Observable<void> {
       return this._organizationsProvider.removeMembership(row.membership).pipe(
          tap(() => {
              for (let i = 0; i < this.dataSource.count; i++) {
                  if (this.dataSource.get(i).membership.id === row.membership.id) {
                      this.dataSource.remove(i);
                      i = this.dataSource.count;
                  }
              }
          }),
        );
    }

    private onError(error: any): void {
        this.isLoading = false;
        this._pendingRolesChange = null;
        this._toaster.open(this._translater.instant('Error_unknown'), '', { duration: 5000 });
    }
}
